/**
 * /agents/runs/:runId — one run with LIVE progress + streamed log events
 * (the "see the logs" view) and the final report once done. Polls via
 * useRunPoller until the run reaches a terminal status.
 */
import React, { useState } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Text,
  Button,
  IconButton,
  Progress,
  Spinner,
  Center,
  Code,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiX } from 'react-icons/fi';
import { callAgents, useRunPoller } from '../../hooks/useAgents';
import AgentStatusChip from '../../components/Agents/AgentStatusChip';

const fmtTime = (s) => (s ? new Date(s).toLocaleTimeString() : '');

const LEVEL_COLOR = { info: 'gray.500', warn: 'orange.400', error: 'red.400' };

export default function AgentRunDetailPage() {
  const { runId } = useParams();
  const history = useHistory();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/runs\/[^/]+$/, '');
  const toast = useToast();
  const [cancelling, setCancelling] = useState(false);

  const { run, events, isPolling } = useRunPoller(runId);

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.400');
  const logBg = useColorModeValue('gray.900', 'black');
  const logColor = useColorModeValue('green.200', 'green.200');

  const cancel = async () => {
    setCancelling(true);
    try {
      await callAgents('cancelRun', { runId });
      toast({ title: 'Cancel requested', status: 'info', duration: 2500 });
    } catch (err) {
      toast({ title: 'Cancel failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setCancelling(false);
    }
  };

  if (!run) {
    return <Center py={20}><Spinner color="blue.400" /></Center>;
  }

  const active = ['queued', 'running'].includes(run.status);
  const p = run.progress || {};
  const pct = p.total ? Math.round(((p.done || 0) / p.total) * 100) : (active ? undefined : 100);
  const totals = run.totals || {};

  return (
    <Box bg={pageBg} minH="100%" p={6}>
      <HStack mb={4} spacing={3}>
        <IconButton aria-label="Back" icon={<FiArrowLeft />} size="sm" variant="ghost" onClick={() => history.push(`${sectionUrl}/runs`)} />
        <Box flex={1}>
          <HStack>
            <Heading size="md">Run</Heading>
            <AgentStatusChip value={run.status} />
            {isPolling && <Spinner size="xs" color="blue.400" />}
          </HStack>
          <Text fontSize="xs" color={subtle}>{run.agent_type} · {run.trigger_source} · {new Date(run.created_at).toLocaleString()}</Text>
        </Box>
        {active && (
          <Button size="sm" leftIcon={<FiX />} colorScheme="red" variant="outline" onClick={cancel} isLoading={cancelling}>Cancel</Button>
        )}
      </HStack>

      {/* Progress */}
      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5} mb={5}>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="sm" fontWeight="medium">{p.phase || run.status}</Text>
          <Text fontSize="sm" color={subtle}>{p.total ? `${p.done || 0}/${p.total}` : ''}</Text>
        </HStack>
        <Progress value={pct} isIndeterminate={active && pct === undefined} size="sm" colorScheme="blue" borderRadius="md" hasStripe={active} isAnimated={active} />
        {run.error && <Text fontSize="sm" color="red.400" mt={3}>Error: {run.error}</Text>}
      </Box>

      {/* Totals */}
      {Object.keys(totals).length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={5}>
          {['mailboxes', 'scanned', 'candidates', 'flagged', 'actioned', 'errors'].filter((k) => totals[k] !== undefined).map((k) => (
            <Stat key={k} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4}>
              <StatLabel color={subtle} textTransform="capitalize">{k}</StatLabel>
              <StatNumber>{totals[k]}</StatNumber>
            </Stat>
          ))}
        </SimpleGrid>
      )}

      <SimpleGrid columns={{ base: 1, lg: run.report ? 2 : 1 }} spacing={5}>
        {/* Logs */}
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
          <Heading size="sm" mb={3}>Logs</Heading>
          <Box bg={logBg} borderRadius="md" p={3} fontFamily="mono" fontSize="xs" maxH="420px" overflowY="auto">
            {events.length === 0 && <Text color="gray.500">No log lines yet.</Text>}
            <VStack align="stretch" spacing={1}>
              {events.map((e) => (
                <HStack key={e.id} align="start" spacing={2}>
                  <Text color="gray.500" flexShrink={0}>{fmtTime(e.created_at)}</Text>
                  <Text color={LEVEL_COLOR[e.level] || logColor} flexShrink={0} textTransform="uppercase" w="44px">{e.level}</Text>
                  <Text color={logColor}>{e.phase ? `[${e.phase}] ` : ''}{e.message}</Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </Box>

        {/* Report */}
        {run.report && (
          <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
            <Heading size="sm" mb={3}>Report</Heading>
            {run.report.output ? (
              <Text fontSize="sm" whiteSpace="pre-wrap">{run.report.output}</Text>
            ) : (
              <Code display="block" whiteSpace="pre-wrap" fontSize="xs" p={3} borderRadius="md" maxH="420px" overflowY="auto">
                {JSON.stringify(run.report, null, 2)}
              </Code>
            )}
          </Box>
        )}
      </SimpleGrid>
    </Box>
  );
}
